import { useEffect, useState } from "react";
import { Check, Save, Send, Loader2, CheckCircle2 } from "lucide-react";
import { ANTROPO_FIELDS } from "@/components/lab/MisurazioniTab";
import * as api from "@/lib/api";
import PortalProgress from "@/components/lab/PortalProgress";

const KEY = "agfit-measures-draft";
const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ date: today(), peso: "", note: "", values: Object.fromEntries(ANTROPO_FIELDS.map((f) => [f, ["", "", ""]])), saved: {} });
const valid = (v) => { const n = parseFloat(String(v).replace(",", ".")); return n > 0 && n < 1000; };
const load = () => { try { return { ...blank(), ...JSON.parse(localStorage.getItem(KEY)) }; } catch (e) { return blank(); } };

function FieldCard({ f, trio, saved, onChange, onSave }) {
  const ok = trio.every(valid);
  return (
    <div className={`rounded-2xl border p-4 ${saved ? "border-[#d4af37]/50 bg-[#d4af37]/5" : "border-[#d4af37]/20 bg-[#0f1d3a]/80"}`} data-testid={`portal-measure-${f}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="font-display text-sm font-bold tracking-wider">{f} <span className="text-[10px] font-normal text-slate-400">(cm)</span></span>
        {saved && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#f1d78a]" data-testid={`portal-measure-${f}-saved`}><Check className="h-3.5 w-3.5" /> Salvato</span>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        {trio.map((v, i) => (
          <input key={i} type="number" inputMode="decimal" value={v} placeholder={`${i + 1}ª`} onChange={(e) => onChange(i, e.target.value)}
            className="lab-input text-center font-mono-num" data-testid={`portal-measure-${f}-${i}`} />
        ))}
      </div>
      {!saved && (
        <button onClick={onSave} disabled={!ok} className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-[#d4af37]/50 bg-[#d4af37]/15 py-2 text-xs font-bold text-[#f1d78a] disabled:opacity-40" data-testid={`portal-measure-${f}-save`}>
          <Save className="h-3.5 w-3.5" /> Salva {f.toLowerCase()}
        </button>
      )}
    </div>
  );
}

export default function PortalMeasures({ last, history = [], onSent }) {
  const [d, setD] = useState(load);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(d)); }, [d]);
  const setTrio = (f, i, v) => setD((x) => ({ ...x, values: { ...x.values, [f]: x.values[f].map((y, j) => (j === i ? v : y)) }, saved: { ...x.saved, [f]: false } }));
  const ready = d.date && valid(d.peso) && ANTROPO_FIELDS.every((f) => d.saved[f]);
  const send = async () => {
    setBusy(true); setErr("");
    try {
      await api.portalSendMeasurements({ date: d.date, peso: d.peso, note: d.note, values: d.values });
      localStorage.removeItem(KEY); setD(blank()); setSent(true); onSent(); window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { setErr(e?.response?.data?.detail || "Invio non riuscito, riprova."); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="portal-measures">
      <PortalProgress history={history} />
      {sent && <div className="flex items-center gap-2 rounded-2xl border border-[#d4af37]/40 bg-[#d4af37]/10 p-4 text-sm text-[#f1d78a]" data-testid="portal-measures-sent"><CheckCircle2 className="h-5 w-5" /> Misurazioni inviate al tuo coach!</div>}
      {!sent && last && <div className="text-xs text-slate-400" data-testid="portal-measures-last">Ultimo invio: misurazioni del {new Date(last.date).toLocaleDateString("it-IT")} · {last.status === "confirmed" ? "confermate dal coach" : "in revisione"}</div>}
      <div className="rounded-2xl border border-[#d4af37]/20 bg-[#0f1d3a]/80 p-4 text-xs text-slate-300">Per ogni parametro misura <b>3 volte</b> e inserisci i 3 valori, poi premi Salva. Quando hai salvato tutto, premi Invia.</div>
      <div className="grid grid-cols-2 gap-3">
        <label className="space-y-1 text-xs text-slate-400">Data<input type="date" value={d.date} onChange={(e) => setD({ ...d, date: e.target.value })} className="lab-input" data-testid="portal-measure-date" /></label>
        <label className="space-y-1 text-xs text-slate-400">Peso (kg)<input type="number" inputMode="decimal" value={d.peso} onChange={(e) => setD({ ...d, peso: e.target.value })} className="lab-input font-mono-num" data-testid="portal-measure-peso" /></label>
      </div>
      {ANTROPO_FIELDS.map((f) => (
        <FieldCard key={f} f={f} trio={d.values[f]} saved={!!d.saved[f]} onChange={(i, v) => setTrio(f, i, v)} onSave={() => setD((x) => ({ ...x, saved: { ...x.saved, [f]: true } }))} />
      ))}
      <textarea rows={2} value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} placeholder="Nota per il coach (facoltativa)" className="lab-input resize-none" data-testid="portal-measure-note" />
      {err && <div className="text-xs text-[hsl(0_72%_68%)]" data-testid="portal-measure-error">{err}</div>}
      <button onClick={send} disabled={!ready || busy} className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#d4af37] to-[#f1d78a] py-4 text-sm font-extrabold tracking-wide text-[#0a1428] disabled:opacity-40" data-testid="portal-measure-send">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} INVIA MISURAZIONI
      </button>
      {!ready && <p className="text-center text-[11px] text-slate-500" data-testid="portal-measure-hint">Compila data, peso e salva tutti i {ANTROPO_FIELDS.length} parametri per inviare.</p>}
    </div>
  );
}
